class CuratedRecommendationTracking extends HTMLElement {
  constructor() {
    super();
    this.viewed = new Set();
    this.viewTimers = new Map();
    this.handleClick = this.handleClick.bind(this);
    this.handleConsent = this.handleConsent.bind(this);
  }

  connectedCallback() {
    if (this.connected) return;
    this.connected = true;
    this.cards = [...this.querySelectorAll('[data-curated-item]')];
    this.addEventListener('click', this.handleClick);
    this.addEventListener('auxclick', this.handleClick);
    document.addEventListener('visitorConsentCollected', this.handleConsent);
    document.addEventListener('visibilitychange', this.handleConsent);
    if (!window.IntersectionObserver) return;

    this.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const card = entry.target;
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) {
          clearTimeout(this.viewTimers.get(card));
          this.viewTimers.delete(card);
        } else if (!this.viewed.has(card.dataset.itemId) && !this.viewTimers.has(card)) {
          this.viewTimers.set(card, setTimeout(() => {
            this.viewTimers.delete(card);
            if (this.connected && document.visibilityState === 'visible') this.recordView(card);
          }, 1000));
        }
      }
    }, { threshold: [0, 0.5] });
    this.cards.forEach((card) => this.observer.observe(card));
  }

  isPreview() {
    return window.Shopify?.theme?.role !== 'main' || !!window.Shopify?.designMode;
  }

  analyticsAllowed() {
    if (this.isPreview()) return false;
    const privacy = window.Shopify?.customerPrivacy;
    try {
      return typeof privacy?.analyticsProcessingAllowed === 'function' && privacy.analyticsProcessingAllowed();
    } catch (_) {
      return false;
    }
  }

  handleConsent() {
    if ((!this.isPreview() && !this.analyticsAllowed()) || !this.observer || document.visibilityState !== 'visible') return;
    // Re-evaluate current visibility after consent, without replaying earlier clicks.
    this.cards.forEach((card) => {
      if (!this.viewed.has(card.dataset.itemId)) {
        this.observer.unobserve(card);
        this.observer.observe(card);
      }
    });
  }

  item(card) {
    return {
      item_id: card.dataset.itemId,
      item_name: card.dataset.itemName,
      item_list_id: 'pdp_curated_recommendations',
      item_list_name: this.dataset.listName,
      index: Number(card.dataset.itemIndex),
      price: Number(card.dataset.itemPrice),
    };
  }

  recordView(card) {
    if (this.viewed.has(card.dataset.itemId)) return;
    if (this.isPreview() || this.analyticsAllowed()) this.viewed.add(card.dataset.itemId);
    this.publish('view_item_list', card);
  }

  handleClick(event) {
    if (event.type === 'auxclick' && event.button !== 1) return;
    if (event.type === 'click' && event.button !== 0) return;
    const link = event.target.closest('a[href]');
    const card = link?.closest('[data-curated-item]');
    if (!card || !this.contains(card)) return;
    const destination = new URL(link.href, window.location.origin);
    if (destination.origin !== window.location.origin || !destination.pathname.endsWith(`/products/${card.dataset.itemHandle}`)) return;
    // A product-link click also proves the card was seen, even before the timer.
    this.recordView(card);
    this.publish('select_item', card);
  }

  publish(name, card) {
    const payload = {
      item_list_id: 'pdp_curated_recommendations',
      item_list_name: this.dataset.listName,
      currency: this.dataset.currency,
      source_product: this.dataset.sourceProduct,
      items: [this.item(card)],
    };
    const allowed = this.analyticsAllowed();
    // Local preview diagnostics never send test interactions to analytics.
    this.dispatchEvent(new CustomEvent('mdh:recommendation-analytics', {
      bubbles: true, detail: { name, payload, suppressed: !allowed },
    }));
    if (!allowed) return;

    try {
      if (typeof window.gtag === 'function') window.gtag('event', name, payload);
    } catch (_) { /* Analytics must never prevent product navigation. */ }
    try {
      if (typeof window.clarity !== 'function') return;
      window.clarity('set', 'mdh_recommendation_source', this.dataset.sourceProduct);
      if (name === 'select_item') {
        window.clarity('set', 'mdh_recommendation_target', card.dataset.itemHandle);
        window.clarity('event', 'curated_recommendation_clicked');
      } else if (!this.clarityViewSent) {
        this.clarityViewSent = true;
        window.clarity('event', 'curated_recommendations_viewed');
      }
    } catch (_) { /* Existing collectors can be unavailable or blocked. */ }
  }

  disconnectedCallback() {
    this.connected = false;
    this.observer?.disconnect();
    this.viewTimers.forEach((timer) => clearTimeout(timer));
    this.viewTimers.clear();
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('auxclick', this.handleClick);
    document.removeEventListener('visitorConsentCollected', this.handleConsent);
    document.removeEventListener('visibilitychange', this.handleConsent);
  }
}

if (!customElements.get('curated-recommendation-tracking')) {
  customElements.define('curated-recommendation-tracking', CuratedRecommendationTracking);
}
